import { useMutation, useQuery } from "@apollo/client";
import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { GetFacilitiesByPersonQuery } from "generated-graphql/graphql";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import invariant from "invariant";
import pluralize from "pluralize";
import { ReactNode } from "react";

type DeleteContactDialogProps = {
  open: boolean;
  onClose: () => void;
  personId: string;
};

type Row = GetFacilitiesByPersonQuery["facilities"]["items"][0];

const GET_PERSON_FOR_DELETION = gql(/* GraphQL */ `
  query GetPersonForDeletion($personId: ID!) {
    person(id: $personId) {
      id
      first
      last
      user {
        id
      }
    }
  }
`);

const GET_FACILITIES_BY_PERSON = gql(/* GraphQL */ `
  query GetFacilitiesByPerson(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        streetAddress1
        streetAddress2
        city
        state
        zip
        country
      }
      count
    }
  }
`);

const DELETE_PERSON_MUTATION = gql(`
mutation DeletePerson($id: ID!) {
  deletePerson(id: $id) {
    id
  }
}
`);

export function DeleteContactDialog({ open, onClose, personId }: DeleteContactDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId not found");

  const { paginationModel, sortModel, columns, setPaginationModel } = useOmnisearchDatagrid<Row>({
    persistenceKey: "delete-contact-dialog",
    isURLDriven: false,
    initialPageSize: 10,
    initialSortModel: [{ field: "name", sort: "asc" }],
    columns: [
      {
        headerName: "Facility",
        field: "name",
        flex: 1,
        filterKeyType: "facility",
      },
      {
        headerName: "Location",
        field: "streetAddress",
        flex: 1,
        valueGetter(_, row) {
          return `${row.city}, ${row.state}`;
        },
      },
    ],
  });

  const {
    data: personData,
    loading: personLoading,
    error: personError,
  } = useQuery(GET_PERSON_FOR_DELETION, {
    variables: { personId },
    skip: !personId,
  });

  const {
    data: facilitiesData,
    previousData: previousFacilitiesData,
    loading: facilitiesLoading,
    error: facilitiesError,
  } = useQuery(GET_FACILITIES_BY_PERSON, {
    variables: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: [...sortModel],
      search: `personId:${personId} tenantId:${tenantId}`,
    },

    skip: !personId,
  });

  const [deletePerson, { loading: deleting }] = useMutation(DELETE_PERSON_MUTATION, {
    refetchQueries: ["Contacts"],
  });

  const handleDelete = async () => {
    try {
      await deletePerson({ variables: { id: personId } });
      alerts.success("Successfully deleted contact");
      onClose();
    } catch (err) {
      alerts.error("There was an error deleting this contact", err);
    }
  };

  const error = personError || facilitiesError;
  const loading =
    personLoading || (!previousFacilitiesData?.facilities.items.length && facilitiesLoading);

  if (error) {
    throw error;
  }

  const associatedFacilities =
    facilitiesData?.facilities?.items ?? previousFacilitiesData?.facilities?.items ?? [];
  const associatedFacilityCount =
    facilitiesData?.facilities?.count ?? previousFacilitiesData?.facilities?.count ?? 0;

  const skeleton = (
    <Stack>
      <Skeleton variant="text" sx={{ width: "30rem" }} />
      <Skeleton variant="text" sx={{ width: "25rem" }} />
      <Skeleton variant="text" sx={{ width: "30rem" }} />
    </Stack>
  );

  const personName = getFullName(personData?.person);

  const facilitiesPlural = pluralize("facilities", associatedFacilityCount);

  const hasUserAccount = Boolean(personData?.person?.user?.id);

  const cannotDeleteMessage = (
    <Typography>'{personName}' is associated with a user account and cannot be deleted.</Typography>
  );

  const confirmDeleteMessage = <Typography>'{personName}' will be permanently deleted.</Typography>;

  const messageToShow = hasUserAccount ? cannotDeleteMessage : confirmDeleteMessage;

  const baseMessage = loading ? skeleton : messageToShow;

  let dialogMessage: ReactNode = baseMessage;

  // If we can delete and there are associated facilities, show the facilities list
  if (associatedFacilityCount > 0) {
    dialogMessage = (
      <Stack spacing={theme.spacing(2)}>
        {baseMessage}
        <Stack sx={{ height: "15rem" }} spacing={theme.spacing(1)}>
          <Typography sx={{ color: theme.palette.grey[500] }}>
            The following {associatedFacilityCount} {facilitiesPlural} will be affected:
          </Typography>
          <DataGrid
            columns={columns}
            rows={associatedFacilities}
            pagination
            sortModel={sortModel}
            sortingMode="server"
            paginationMode="server"
            initialState={{
              pagination: {
                paginationModel,
              },
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
            rowCount={associatedFacilityCount}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <ConfirmDialog
      open={open}
      header="Are you sure you want to delete this contact?"
      resources={[
        {
          resourceType: "Contact",
          resourceName: personName,
        },
      ]}
      info={dialogMessage}
      loading={loading || deleting}
      disableConfirm={hasUserAccount}
      onClose={onClose}
      onConfirm={handleDelete}
      cancelText="Cancel"
    />
  );
}
